import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect, useRouter } from 'expo-router';
import { HeaderHeightContext } from 'expo-router/react-navigation';
import { List, X } from 'lucide-react-native';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Platform, type ScrollView as RNScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import {
  AskSummaryBody,
  AskStartInquiryButton,
  AskSummaryHeader,
  AskSummarySheet,
} from './AskCaseContext';
import {
  AskConversationDialog,
  useAskConversationTitle,
  type AskConversationDialogKind,
} from './AskConversationActions';
import { useAskHeader, useIsAskHeaderTransparent } from './AskHeader';
import { HistoryDrawer } from './HistoryDrawer';
import { LibraryChatComposer, type LibraryChatComposerHandle } from './LibraryChatComposer';
import {
  AskConversationProvider,
  AskUiProvider,
  useAskConversationContext,
  useAskUiStore,
  useAskUiStoreApi,
} from './LibraryChatContext';
import { LibraryChatHistory } from './LibraryChatHistory';
import { LibraryChatEmptyState, LibraryChatTurnItem } from './LibraryChatThread';
import { RelatedInquiryDetail } from './RelatedInquiryDetail';
import { setPendingResults } from './pendingResultHandoff';
import { ResearchTourZone } from './researchTutorial/ResearchTourZone';
import { useResearchTutorialStore } from './researchTutorial/researchTutorialContext';
import { buildResultStep, type StepDirection } from './resultStep';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
} from '@/components/ui/actionsheet';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { KeyboardSpacer } from '@/components/ui/keyboard-spacer';
import { Pressable } from '@/components/ui/pressable';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { ONE_SECOND } from '@/constants/time';
import {
  useAskConversationController,
  type AskConversationController,
} from '@/hooks/ask/useAskConversationController';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useColors } from '@/hooks/useColors';
import { useIsKeyboardVisible } from '@/hooks/useIsKeyboardVisible';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import type { AskResult } from '@/services/ask/api';
import {
  isActiveTurn,
  isRepeatResult,
  selectHandoffTurn,
  selectNavigableResults,
  selectSelectedResult,
  type NavigableResult,
} from '@/services/ask/selectors';
import type { AskNotice } from '@/services/stores/askUiStore';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import {
  trackLibraryChatResultPostStepped,
  trackLibraryChatResultViewed,
} from '@/util/analyticsEvents';

type SheetProps = {
  isOpen: boolean;
  onClose: () => void;
  controller: AskConversationController;
  children: React.ReactNode;
};

/**
 * The compact-layout sheet holding the selected result's inquiry.
 *
 * The Actionsheet renders its content through the overlay portal at the app
 * root, outside this screen's tree, so neither the UI store nor the
 * conversation reaches it. The sheet provides both again inside the portal;
 * without them the first thing in the sheet to read either throws and takes
 * the screen down.
 */
function MobileSheet({ isOpen, onClose, controller, children }: SheetProps) {
  const { bottom } = useSafeAreaInsets();

  const store = useAskUiStoreApi();

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />

      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ minHeight: '85%', maxHeight: '95%', paddingBottom: bottom }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <AskUiProvider value={store}>
          <AskConversationProvider value={controller}>
            <VStack className="w-full flex-1">{children}</VStack>
          </AskConversationProvider>
        </AskUiProvider>
      </ActionsheetContent>
    </Actionsheet>
  );
}

/**
 * Moves the selection to a result the reader stepped to with Previous/Next,
 * and reports it. Selecting it the way tapping its card does keeps the
 * thread's highlight and the result-viewed report in step with the footer.
 * The one path for every presentation: the sheet and pane call it directly,
 * and the iOS result screen reaches it through the result handoff.
 */
function stepToResult(
  store: ReturnType<typeof useAskUiStoreApi>,
  conversationId: string,
  turnId: string,
  { result, position }: NavigableResult,
  direction: StepDirection
) {
  trackLibraryChatResultPostStepped({
    conversation_id: conversationId,
    turn_id: turnId,
    source: result.source,
    result_id: result.resultId,
    direction,
  });

  trackLibraryChatResultViewed({
    conversation_id: conversationId,
    turn_id: turnId,
    source: result.source,
    result_id: result.resultId,
    position,
    is_repeat: isRepeatResult(result, turnId),
  });

  store.getState().selectResult({
    conversationId,
    turnId,
    source: result.source,
    resultId: result.resultId,
  });
}

/** The selected result, with Previous/Next stepping the selection through the same turn's results. */
function SelectedResultDetail() {
  const { conversationId, snapshot } = useAskConversationContext();

  const store = useAskUiStoreApi();

  const selected = useAskUiStore((state) => state.selected);

  const hasResults = snapshot?.turns.some((turn) => turn.results.length > 0) ?? false;

  const selection = selected?.conversationId === conversationId ? selected : null;

  const step = selection
    ? buildResultStep(
        selectNavigableResults(snapshot, selection.turnId),
        selection.resultId,
        (target, direction) =>
          stepToResult(store, conversationId, selection.turnId, target, direction)
      )
    : null;

  return (
    <RelatedInquiryDetail
      selection={selection}
      result={selectSelectedResult(snapshot, selection)}
      hasResults={hasResults}
      step={step}
    />
  );
}

const NOTICE_COPY: Record<AskNotice, string> = {
  not_found: "That research topic isn't available. It may have been deleted.",
  deleted_elsewhere: 'That research topic was deleted on another device.',
  conversation_full:
    'That topic reached its limit, so a new research topic starts with your question.',
};

/**
 * Says once why the reader landed on a new conversation, then gets out of the
 * way. It clears when the reader sends, dismisses it, or moves anywhere from
 * history. Every notice sends the reader to a new conversation, so a saved one
 * never shows it, even while the store still holds it.
 */
function ArrivalNotice() {
  const store = useAskUiStoreApi();

  const { isNew } = useAskConversationContext();

  const notice = useAskUiStore((state) => state.notice);

  if (!notice || !isNew) {
    return null;
  }

  return (
    <HStack
      className="items-center gap-2 rounded-xl border border-outline-500 bg-background-50 px-3 py-2"
      accessibilityLiveRegion="polite">
      <Text className="flex-1 text-sm text-typography-900">{NOTICE_COPY[notice]}</Text>

      <Pressable
        onPress={() => store.getState().setNotice(null)}
        hitSlop={ICON_BUTTON_HIT_SLOP}
        accessibilityRole="button"
        accessibilityLabel="Dismiss"
        className="h-10 w-10 items-center justify-center rounded-full web:cursor-pointer web:hover:bg-background-100">
        <Icon as={X} className="h-4 w-4 text-typography-700" />
      </Pressable>
    </HStack>
  );
}

/** Space between turns. Offsets are computed from it, so the column's gap must come from here. */
const TURN_GAP = 32;

type TurnLayout = {
  top: number | null;
  heights: Map<string, number>;
};

/**
 * Where a turn starts inside the scroll content, built from where the first
 * turn starts plus the measured heights of the turns above it.
 *
 * Each turn's own `onLayout` y would be simpler and is wrong on web:
 * react-native-web reports layout only when an element changes size, so a turn
 * pushed down by an earlier one growing keeps its old y. Heights do change
 * when a turn grows, so summing them stays true on both platforms.
 */
function turnOffset(
  turns: readonly { id: string }[],
  layout: TurnLayout,
  turnId: string
): number | null {
  if (layout.top == null) {
    return null;
  }

  let offset = layout.top;

  for (const turn of turns) {
    if (turn.id === turnId) {
      return offset;
    }

    const height = layout.heights.get(turn.id);

    if (height == null) {
      return null;
    }

    offset += height + TURN_GAP;
  }

  return null;
}

type ScrollAim = { kind: 'end' } | { kind: 'turn'; turnId: string };

/** The column's own vertical padding, matching its `py-6`; floating chrome adds to it. */
const CONTENT_PADDING = 24;

/**
 * Height of the gradient that eases scrolled text into the background, on
 * compact layouts: above the composer on web, and at the screen's bottom edge
 * behind the floating composer on native. The composer is a view we draw
 * ourselves, so it needs the fade painted by hand. Under
 * Liquid Glass the thread scrolls behind the floating composer, so the fade
 * runs from the screen's edge at twice this height. Elsewhere on native the
 * composer sits in layout below the thread, so it fades just above it, as web.
 */
const COMPOSER_FADE_HEIGHT = 56;

/** Space between the composer and an open keyboard, in place of the home-indicator inset. */
const KEYBOARD_GAP = 10;

/**
 * The conversation. A new turn scrolls to the bottom so its progress is in
 * view; when a turn then finishes, the view moves to the top of that turn so
 * its results read downward from it. Without the second scroll the results
 * land below the fold and the screen looks unchanged.
 *
 * Floating chrome is cleared by padding the content, not by the system's
 * automatic insets. Those make the resting offset negative, which the scroll
 * targets here would not account for, and add the home-indicator inset on top
 * of a composer that already pads itself clear of it. Padding keeps zero as
 * the top, so every target only has to step back by the header's height.
 *
 * The floating composer dock grows when a turn finishes, since the Start an
 * inquiry pill appears in it, and that lands after the scroll to the turn. So
 * the last scroll the thread made is kept and re-run once, on the next dock
 * resize, which lifts the turn's end clear of the pill. It is then dropped, as
 * it is on a drag: later resizes (the keyboard, a wrapping draft, the terms
 * reveal) must not pull the thread back from wherever the reader has since
 * moved it, and not every way of moving it reports a drag.
 */
function ChatColumn({
  composerRef,
  onSelectResult,
  topInset,
  bottomInset,
}: {
  composerRef: React.RefObject<LibraryChatComposerHandle | null>;
  onSelectResult: (turnId: string, result: AskResult, position: number) => void;
  /** Height of a header floating over the column, which the first turn must start below. */
  topInset: number;
  /** Height of a composer floating over the column, which the last turn must scroll clear of. */
  bottomInset: number;
}) {
  const { conversationId, snapshot, isLoading, loadError, reload } = useAskConversationContext();

  const turns = snapshot?.turns ?? [];

  /**
   * The conversation whose opening scroll has run. A cached conversation has its
   * turns at mount, so the turn count never grows and the growth check alone
   * would leave a reopened conversation at the top. A conversation with no turns
   * counts as opened, so its first turn takes the growth path and animates in.
   */
  const openedConversationId = useRef<string | null>(null);

  /**
   * Set after the opening scroll so content that finishes laying out late, such
   * as result cards, keeps the thread at the bottom. Dropped after a moment or
   * on the reader's first drag, so it never pulls back a reader who has moved on.
   */
  const isFollowingEnd = useRef(false);

  const followEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollRef = useRef<RNScrollView>(null);

  const turnLayout = useRef<TurnLayout>({ top: null, heights: new Map() });

  const turnCountRef = useRef(turns.length);

  const activeIdsRef = useRef<string[]>([]);

  const scrollAim = useRef<ScrollAim | null>(null);

  const scrollSize = useRef({ viewport: 0, content: 0 });

  const insetRef = useRef(bottomInset);

  const aimScroll = (aim: ScrollAim) => {
    scrollAim.current = aim;

    if (aim.kind === 'end') {
      scrollRef.current?.scrollToEnd({ animated: true });

      return;
    }

    const offset = turnOffset(turns, turnLayout.current, aim.turnId);

    if (offset == null) {
      return;
    }

    const { viewport, content } = scrollSize.current;

    scrollRef.current?.scrollTo({
      y: Math.min(Math.max(offset - 16 - topInset, 0), Math.max(content - viewport, 0)),
      animated: true,
    });
  };

  const stopFollowingEnd = () => {
    isFollowingEnd.current = false;

    if (followEndTimer.current) {
      clearTimeout(followEndTimer.current);

      followEndTimer.current = null;
    }
  };

  useEffect(() => stopFollowingEnd, []);

  const handleContentSizeChange = (_width: number, height: number) => {
    scrollSize.current.content = height;

    const activeIds = turns.filter(isActiveTurn).map((turn) => turn.id);

    const insetChanged = bottomInset !== insetRef.current;

    insetRef.current = bottomInset;

    const settledId = activeIdsRef.current.find(
      (turnId) => !activeIds.includes(turnId) && turns.some((turn) => turn.id === turnId)
    );

    if (turns.length === 0) {
      openedConversationId.current = conversationId;
    }

    const isOpening = turns.length > 0 && openedConversationId.current !== conversationId;

    if (isOpening) {
      openedConversationId.current = conversationId;

      scrollAim.current = { kind: 'end' };

      scrollRef.current?.scrollToEnd({ animated: false });

      stopFollowingEnd();

      isFollowingEnd.current = true;

      followEndTimer.current = setTimeout(stopFollowingEnd, ONE_SECOND);
    } else if (turns.length > turnCountRef.current) {
      stopFollowingEnd();

      aimScroll({ kind: 'end' });
    } else if (settledId) {
      stopFollowingEnd();

      aimScroll({ kind: 'turn', turnId: settledId });
    } else if (insetChanged && scrollAim.current) {
      aimScroll(scrollAim.current);

      scrollAim.current = null;
    } else if (isFollowingEnd.current) {
      scrollRef.current?.scrollToEnd({ animated: false });
    }

    turnCountRef.current = turns.length;

    activeIdsRef.current = activeIds;
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <VStack className="flex-1 items-center justify-center">
          <Spinner accessibilityLabel="Loading the research topic" />
        </VStack>
      );
    }

    if (loadError && !snapshot) {
      return (
        <VStack className="flex-1 items-center justify-center gap-3">
          <Text className="text-center text-sm text-typography-700">
            This conversation couldn't be loaded.
          </Text>

          <Button size="sm" variant="outline" action="secondary" onPress={reload}>
            <ButtonText>Try again</ButtonText>
          </Button>
        </VStack>
      );
    }

    if (turns.length === 0) {
      return <LibraryChatEmptyState onPickExample={(text) => composerRef.current?.setText(text)} />;
    }

    return turns.map((turn, index) => (
      <View
        key={turn.id}
        onLayout={(event) => {
          const { y, height } = event.nativeEvent.layout;

          turnLayout.current.heights.set(turn.id, height);

          if (index === 0) {
            turnLayout.current.top = y;
          }
        }}>
        <LibraryChatTurnItem turn={turn} onSelectResult={onSelectResult} />
      </View>
    ));
  };

  return (
    <ScrollView
      ref={scrollRef}
      className="flex-1"
      contentContainerClassName="mx-auto w-full max-w-3xl flex-grow px-4 py-6 md:px-8"
      contentContainerStyle={{
        gap: TURN_GAP,
        ...(topInset > 0 ? { paddingTop: topInset + CONTENT_PADDING } : null),
        ...(bottomInset > 0 ? { paddingBottom: bottomInset + CONTENT_PADDING } : null),
      }}
      scrollIndicatorInsets={{ top: topInset, bottom: bottomInset }}
      keyboardShouldPersistTaps="handled"
      contentInsetAdjustmentBehavior="never"
      onLayout={(event) => {
        scrollSize.current.viewport = event.nativeEvent.layout.height;
      }}
      onScrollBeginDrag={() => {
        scrollAim.current = null;

        stopFollowingEnd();
      }}
      onContentSizeChange={handleContentSizeChange}>
      <ArrivalNotice />

      {renderBody()}
    </ScrollView>
  );
}

type SummaryControlProps = {
  isSummaryOpen: boolean;
  onSummaryOpenChange: (isOpen: boolean) => void;
};

/** The latest completed turn and the summary the planner wrote for it, when it has one. */
function useLatestTurnSummary() {
  const { latestCompletedTurn } = useAskConversationContext();

  const context = latestCompletedTurn?.context;

  return latestCompletedTurn && context ? { turn: latestCompletedTurn, context } : null;
}

/**
 * The "Start an inquiry" pill above the composer. Hidden while a send is in
 * flight or the newest turn has not completed, so it never hands off an older
 * question than the one on screen. A turn with no summary hands off what the
 * reader typed, so the way to an expert never disappears with the summary.
 */
function StartInquirySlot() {
  const { conversationId, snapshot, isSending } = useAskConversationContext();

  const tutorialConversationId = useResearchTutorialStore((state) => state.conversationId);

  const turn = selectHandoffTurn(snapshot);

  if (!turn || isSending) {
    return null;
  }

  return (
    <ResearchTourZone
      segment="results"
      zone={3}
      active={tutorialConversationId === conversationId && snapshot?.turns[0]?.id === turn.id}
      hug>
      <AskStartInquiryButton
        conversationId={conversationId}
        turnId={turn.id}
        questionText={turn.context?.question ?? turn.text}
      />
    </ResearchTourZone>
  );
}

/**
 * The desktop detail pane: the summary when the reader opened it, otherwise
 * the selected result's inquiry.
 *
 * The inquiry stays mounted beneath the summary and is only hidden, so closing
 * the summary returns to the result the reader was on and where they had
 * scrolled.
 */
function DetailPane({ isSummaryOpen, onSummaryOpenChange }: SummaryControlProps) {
  const { conversationId } = useAskConversationContext();

  const summary = useLatestTurnSummary();

  const showsSummary = isSummaryOpen && summary !== null;

  return (
    <>
      <VStack className={twJoin('min-h-0 flex-1', showsSummary && 'hidden')}>
        <SelectedResultDetail />
      </VStack>

      {showsSummary ? (
        <ScrollView className="flex-1" contentContainerClassName="p-5">
          <VStack className="gap-2" accessibilityLiveRegion="polite">
            <AskSummaryHeader onClose={() => onSummaryOpenChange(false)} />

            <AskSummaryBody
              context={summary.context}
              turnId={summary.turn.id}
              conversationId={conversationId}
            />
          </VStack>
        </ScrollView>
      ) : null}
    </>
  );
}

/** The summary on compact layouts, which have no detail pane. */
function SummarySheet({ isSummaryOpen, onSummaryOpenChange }: SummaryControlProps) {
  const { conversationId } = useAskConversationContext();

  const summary = useLatestTurnSummary();

  if (!summary) {
    return null;
  }

  return (
    <AskSummarySheet
      context={summary.context}
      turnId={summary.turn.id}
      conversationId={conversationId}
      isOpen={isSummaryOpen}
      onClose={() => onSummaryOpenChange(false)}
    />
  );
}

/**
 * Keeps the desktop detail column from sitting blank: when nothing in this
 * conversation is selected, the newest completed turn's first available result
 * is. It reports no analytics, since the reader chose nothing, and on compact
 * layouts it opens no sheet, which would cover the results as they arrive.
 */
function useDefaultSelection(controller: AskConversationController) {
  const store = useAskUiStoreApi();

  const selected = useAskUiStore((state) => state.selected);

  const { conversationId, latestCompletedTurn } = controller;

  useEffect(() => {
    if (selected?.conversationId === conversationId || !latestCompletedTurn) {
      return;
    }

    const first = latestCompletedTurn.results.find((result) => !result.unavailable);

    if (first) {
      store.getState().selectResult({
        conversationId,
        turnId: latestCompletedTurn.id,
        source: first.source,
        resultId: first.resultId,
      });
    }
  }, [conversationId, latestCompletedTurn, selected, store]);
}

/**
 * Hands the conversation's title and controls to the navigation header, which
 * the Ask layout draws, and holds the rename and delete dialogs its menu opens.
 * Rendered inside the conversation provider, which the header host sits above.
 */
function HeaderBridge({
  onShowHistory,
  onShowSummary,
  onClose,
}: {
  onShowHistory: () => void;
  onShowSummary: () => void;
  onClose: () => void;
}) {
  const { title, isTopicTitle, canManage } = useAskConversationTitle();

  const hasSummary = useLatestTurnSummary() !== null;

  const isCompact = useIsCompactLayout();

  const [dialog, setDialog] = useState<AskConversationDialogKind | null>(null);

  useAskHeader({
    title,
    isTopicTitle,
    canManage,
    hasSummary,
    showsHistoryButton: isCompact,
    onShowHistory,
    onShowSummary,
    onRename: () => setDialog('rename'),
    onDelete: () => setDialog('delete'),
    onClose,
  });

  return <AskConversationDialog dialog={dialog} onClose={() => setDialog(null)} />;
}

/**
 * The desktop history column, which folds to a rail so the detail pane can
 * widen.
 *
 * One toggle serves both states and stays at the same place in the tree, so
 * it is the same element before and after a press. Swapping in a different
 * button would unmount the one holding focus and drop a keyboard reader back
 * to the top of the page. It sits top-left in both states, so it does not
 * jump as the column folds.
 */
function HistoryColumn({
  isCollapsed,
  onCollapsedChange,
}: {
  isCollapsed: boolean;
  onCollapsedChange: (isCollapsed: boolean) => void;
}) {
  return (
    <VStack
      className={twJoin(
        'relative hidden border-r border-outline-200 bg-background-50 md:flex',
        isCollapsed ? 'w-14' : 'w-72'
      )}>
      <Pressable
        onPress={() => onCollapsedChange(!isCollapsed)}
        accessibilityRole="button"
        accessibilityLabel={
          isCollapsed ? 'Show previous conversations' : 'Hide previous conversations'
        }
        className="absolute left-2 top-2 z-10 h-10 w-10 items-center justify-center rounded-full web:cursor-pointer web:hover:bg-background-100">
        <Icon as={List} className="h-5 w-5 text-typography-700" />
      </Pressable>

      {isCollapsed ? null : <LibraryChatHistory hasLeadingToggle />}
    </VStack>
  );
}

/**
 * Research: a saved, multi-turn research topic over the Inquiry Library.
 *
 * Desktop is three columns — the reader's conversations, this conversation,
 * and the selected result's Inquiry Library post — so answers can be compared
 * without losing the place. A new topic's first question landing folds the
 * history to a rail and widens the post's column, so the whole inquiry can be
 * read; the column's toggle brings the history back or folds it by hand.
 * Below `md` it is the conversation alone; history slides in from the side and
 * the post opens as a sheet.
 *
 * Every turn comes from the conversation snapshot, which the realtime hints,
 * polling and focus refetches keep current, so the screen shows the same
 * conversation on every device. Focus re-reads it rather than clearing
 * anything: history is saved by design now, and what must not outlive a
 * session is handled by sign-out clearing the cache.
 *
 * On compact layouts the post sheet opens only on a reader's tap.
 *
 * The title and controls are the navigation header's on every platform, with
 * rename and delete in a menu; on iOS they are system bar buttons. Where Liquid
 * Glass is drawn (iOS 26) that header turns transparent on a phone and the
 * composer floats over the thread as glass, so answers scroll beneath both the
 * way they do in the system's own apps. The thread is padded
 * by the composer's measured height so its last turn still clears it.
 * Everywhere else, and under Reduce Transparency, the composer sits below the
 * thread on the screen's own background.
 */
export function LibraryChatScreen({
  conversationId,
  isNew,
}: {
  conversationId: string;
  isNew: boolean;
}) {
  const controller = useAskConversationController(conversationId, isNew);

  const router = useRouter();

  const store = useAskUiStoreApi();

  const isCompact = useIsCompactLayout();

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const isKeyboardVisible = useIsKeyboardVisible();

  const { navigateBack } = useBackNavigation(ROUTES.COMMUNITY);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const [isDetailOpen, setIsDetailOpen] = useState(false);

  /*
   * Folded when a new topic's first question lands, and by the column's toggle.
   * Held in the Ask store rather than here, because each conversation remounts
   * this screen and a folded column must stay folded. The detail pane widens
   * into the space it gives up.
   */
  const isHistoryCollapsed = useAskUiStore((state) => state.isHistoryCollapsed);

  const setIsHistoryCollapsed = useAskUiStore((state) => state.setHistoryCollapsed);

  /*
   * The layout the summary was opened in. The summary lives in the detail pane
   * on desktop and in a sheet on compact layouts, so crossing `md` closes it
   * rather than carrying it over; a sheet that popped back open after a resize
   * would appear without the reader asking. Kept here rather than in the
   * control so a turn completing underneath does not close it mid-read.
   */
  const [summaryOpenedCompact, setSummaryOpenedCompact] = useState<boolean | null>(null);

  if (summaryOpenedCompact !== null && summaryOpenedCompact !== isCompact) {
    setSummaryOpenedCompact(null);
  }

  const isSummaryOpen = summaryOpenedCompact === isCompact;

  const setIsSummaryOpen = useCallback(
    (isOpen: boolean) => setSummaryOpenedCompact(isOpen ? isCompact : null),
    [isCompact]
  );

  const hasGlass = useLiquidGlass();

  const isHeaderTransparent = useIsAskHeaderTransparent();

  /*
   * The native header's height. Under the transparent header it overlaps the
   * thread, which pads itself clear of it.
   */
  const headerHeight = useContext(HeaderHeightContext) ?? 0;

  const [dockHeight, setDockHeight] = useState(0);

  const composerRef = useRef<LibraryChatComposerHandle>(null);

  const { reload } = controller;

  const hasFocusedOnce = useRef(false);

  /*
   * Re-read on returning to the screen, not on arriving: the snapshot query
   * already fetches on mount, and a second `refetch` there would cancel it and
   * send the same request again.
   */
  useFocusEffect(
    useCallback(() => {
      if (!hasFocusedOnce.current) {
        hasFocusedOnce.current = true;

        return;
      }

      if (!isNew) {
        reload();
      }
    }, [isNew, reload])
  );

  useDefaultSelection(controller);

  /**
   * The latest snapshot, read when a result is tapped. A ref rather than a
   * dependency, so the tap handler keeps one identity while a running turn
   * updates the snapshot, and the memoised turns below it do not re-render.
   */
  const snapshotRef = useRef(controller.snapshot);

  useEffect(() => {
    snapshotRef.current = controller.snapshot;
  });

  const handleSelectResult = useCallback(
    (turnId: string, result: AskResult, position: number) => {
      store.getState().selectResult({
        conversationId,
        turnId,
        source: result.source,
        resultId: result.resultId,
      });

      setSummaryOpenedCompact(null);

      trackLibraryChatResultViewed({
        conversation_id: conversationId,
        turn_id: turnId,
        source: result.source,
        result_id: result.resultId,
        position,
        is_repeat: isRepeatResult(result, turnId),
      });

      if (isCompact) {
        /*
         * Every compact layout pushes a real screen for the result, so back,
         * swipe and the hardware back button return here, and on iOS its Save
         * toggle lives in a native Stack.Toolbar that morphs with this
         * screen's bar buttons (see ROUTES.LIBRARY_CHAT_RESULT). Non-inquiry
         * sources have no post list to show there yet, so they fall back to
         * the sheet, which already renders the "can't be opened" message for
         * them via RelatedInquiryDetail.
         */
        if (!result.unavailable && result.source === 'inquiry') {
          setPendingResults(result.resultId, {
            results: selectNavigableResults(snapshotRef.current, turnId),
            onStep: (target, direction) =>
              stepToResult(store, conversationId, turnId, target, direction),
          });

          router.push(
            ROUTES.LIBRARY_CHAT_RESULT({
              conversationId,
              turnId,
              resultId: result.resultId,
              source: result.source,
            })
          );
        } else {
          setIsDetailOpen(true);
        }
      }
    },
    [conversationId, isCompact, router, store]
  );

  const hasSentThisVisit = (controller.snapshot?.turns.length ?? 0) > 0;

  useEffect(() => {
    if (hasSentThisVisit && isNew) {
      store.getState().setNotice(null);
    }
  }, [hasSentThisVisit, isNew, store]);

  return (
    <AskConversationProvider value={controller}>
      <View className="flex-1 bg-background-50">
        <HeaderBridge
          onShowHistory={() => setIsHistoryOpen(true)}
          onShowSummary={() => setIsSummaryOpen(true)}
          onClose={navigateBack}
        />

        <Box className="min-h-0 flex-1 flex-row">
          <HistoryColumn
            isCollapsed={isHistoryCollapsed}
            onCollapsedChange={setIsHistoryCollapsed}
          />

          <VStack className="relative min-w-0 flex-1">
            <View className="relative min-h-0 flex-1">
              <ChatColumn
                composerRef={composerRef}
                onSelectResult={handleSelectResult}
                topInset={isHeaderTransparent ? headerHeight : 0}
                bottomInset={hasGlass ? dockHeight : 0}
              />

              {/*
               * Web's composer never floats, so its fade is anchored to the
               * scroll column's own bottom edge, where the composer actually
               * starts. Native paints it at the bottom of the dock below, behind
               * the composer, so it sits on the screen's true bottom edge when
               * that dock floats as Liquid Glass.
               */}
              {Platform.OS === 'web' && isCompact ? (
                <LinearGradient
                  colors={[`${colors.background[50]}00`, colors.background[50]]}
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: COMPOSER_FADE_HEIGHT,
                  }}
                />
              ) : null}
            </View>

            <View
              className={twJoin(hasGlass && 'absolute bottom-0 left-0 right-0 items-center')}
              onLayout={
                hasGlass ? (event) => setDockHeight(event.nativeEvent.layout.height) : undefined
              }>
              {Platform.OS !== 'web' && isCompact ? (
                <LinearGradient
                  colors={[`${colors.background[50]}00`, colors.background[50]]}
                  pointerEvents="none"
                  style={
                    hasGlass
                      ? {
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: COMPOSER_FADE_HEIGHT * 2,
                        }
                      : {
                          position: 'absolute',
                          top: -COMPOSER_FADE_HEIGHT,
                          left: 0,
                          right: 0,
                          height: COMPOSER_FADE_HEIGHT,
                        }
                  }
                />
              ) : null}

              <VStack
                className="mx-auto w-full max-w-3xl gap-2 px-4 pt-2 md:px-8"
                style={{ paddingBottom: isKeyboardVisible ? KEYBOARD_GAP : Math.max(bottom, 12) }}>
                <StartInquirySlot />

                <LibraryChatComposer ref={composerRef} />
              </VStack>
            </View>
          </VStack>

          <VStack
            className={twJoin(
              'hidden w-[400px] border-l border-outline-200 bg-background-50 md:flex',
              isHistoryCollapsed && 'lg:w-1/2'
            )}>
            <DetailPane isSummaryOpen={isSummaryOpen} onSummaryOpenChange={setIsSummaryOpen} />
          </VStack>
        </Box>

        {Platform.OS === 'web' ? null : <KeyboardSpacer />}
      </View>

      {isCompact ? (
        <>
          <HistoryDrawer
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            controller={controller}
          />

          <MobileSheet
            isOpen={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            controller={controller}>
            <SelectedResultDetail />
          </MobileSheet>

          <SummarySheet isSummaryOpen={isSummaryOpen} onSummaryOpenChange={setIsSummaryOpen} />
        </>
      ) : null}
    </AskConversationProvider>
  );
}
